import React, { useState, useEffect, useRef } from "react";
import { 
  Scale, 
  Thermometer,
  AlertTriangle,
  Plus,
  Search,
  FileText,
  CheckCircle2,
  XCircle,
  Calendar,
  Download,
  Trash2,
  RefreshCw,
  Loader2,
  Clock,
  Settings2,
  Sparkles,
  ClipboardCheck,
  Pencil,
  Upload,
  FileDown,
  Filter,
  Eye,
  Table as TableIcon,
  Printer,
  X
} from "lucide-react";
import { Layout } from "../components/Layout";
import { RelatorioFilterModal } from "../components/RelatorioFilterModal";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { apiRequest, formatDate, API_URL, getToken, hojeBrasil, horaBrasil } from "../lib/utils";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "../components/ToastProvider";
import { ConfirmacaoRegistroDialog } from "../components/ConfirmacaoRegistroDialog";
import { avaliarFaixa, temFaixa, descreverFaixa } from "../components/FaixaPermitidaTermo";
import { ProgramacaoRotina, descreverProgramacao, DIAS_SEG_SEX } from "../components/ProgramacaoRotina";
import { useAuth } from "../context/AuthContext";
import { AdminPasswordDialog } from "../components/AdminPasswordDialog";

const CORES = [
  { value: "slate", label: "Cinza", class: "bg-slate-50 text-slate-600" },
  { value: "teal", label: "Verde-azulado", class: "bg-teal-50 text-teal-600" },
  { value: "amber", label: "Âmbar", class: "bg-amber-50 text-amber-600" },
  { value: "blue", label: "Azul", class: "bg-blue-50 text-blue-600" },
  { value: "emerald", label: "Verde", class: "bg-emerald-50 text-emerald-600" },
  { value: "purple", label: "Roxo", class: "bg-purple-50 text-purple-600" },
  { value: "pink", label: "Rosa", class: "bg-pink-50 text-pink-600" },
  { value: "orange", label: "Laranja", class: "bg-orange-50 text-orange-600" },
];

const getCorClass = (cor) => CORES.find(c => c.value === cor)?.class || CORES[0].class;

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorClasses = {
    teal: "bg-teal-50 text-teal-600",
    emerald: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    blue: "bg-blue-50 text-blue-600",
    amber: "bg-amber-50 text-amber-600",
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-sm text-slate-500">{title}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const initialBalancaForm = () => ({
  item_id: "",
  data_verificacao: hojeBrasil(),
  hora_verificacao: horaBrasil(),
  peso_padrao: "",
  peso_indicado: "",
  diferenca: "",
  conforme: true,
  responsavel: "",
  observacoes: "",
});

const initialTermoForm = () => ({
  item_id: "",
  data_verificacao: hojeBrasil(),
  hora_verificacao: horaBrasil(),
  temperatura_lida: "",
  umidade_lida: "",
  temperatura_min: "",
  temperatura_max: "",
  umidade_min: "",
  umidade_max: "",
  conforme: true,
  responsavel: "",
  observacoes: "",
});

const initialTipoForm = {
  nome: "",
  descricao: "",
  periodicidade: "diaria_1x",
  dias_semana: DIAS_SEG_SEX,
  dia_semana: "seg",
  dia_mes: 1,
  area: "",
  campos: [],
  cor: "slate",
  ativo: true,
  tipo_sistema: null,
  arquivo_url: "",
  arquivo_nome: "",
};

const PERIODICIDADES_ROTINA = [
  { value: "diaria_1x", label: "Diaria (1x ao dia)" },
  { value: "diaria_2x", label: "Diaria (2x ao dia)" },
  { value: "semanal", label: "Semanal" },
  { value: "quinzenal", label: "Quinzenal" },
  { value: "mensal", label: "Mensal" },
];

const initialCampoForm = {
  nome: "",
  tipo: "text",
  obrigatorio: false,
  opcoes: "",
};

export const Verificações = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [verificacoes, setVerificacoes] = useState([]);
  const [itens, setItens] = useState([]);
  const [tiposCustom, setTiposCustom] = useState([]);
  const [setores, setSetores] = useState([]);
  
  // Filtros
  const [tipoFiltro, setTipoFiltro] = useState("all");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  
  // Dialogs
  const [balancaDialogOpen, setBalancaDialogOpen] = useState(false);
  const [termoDialogOpen, setTermoDialogOpen] = useState(false);
  const [tipoDialogOpen, setTipoDialogOpen] = useState(false);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteTipoId, setDeleteTipoId] = useState(null);
  const [editingTipoId, setEditingTipoId] = useState(null);
  const [relatorioModalOpen, setRelatorioModalOpen] = useState(false);
  const [visualizarModalOpen, setVisualizarModalOpen] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPdfDireto, setExportandoPdfDireto] = useState(false);
  
  // Forms
  const [balancaForm, setBalancaForm] = useState(initialBalancaForm());
  const [termoForm, setTermoForm] = useState(initialTermoForm());
  const termoItemSelecionado = itens.find((i) => i.id === termoForm.item_id);
  const termoForaFaixa = termoItemSelecionado ? avaliarFaixa(termoItemSelecionado, termoForm) : [];
  const atualizarTermo = (patch) => {
    const novo = { ...termoForm, ...patch };
    const item = itens.find((i) => i.id === novo.item_id);
    if (item && temFaixa(item) && ("item_id" in patch || "temperatura_lida" in patch || "temperatura_min" in patch || "temperatura_max" in patch || "umidade_lida" in patch)) {
      novo.conforme = avaliarFaixa(item, novo).length === 0;
    }
    setTermoForm(novo);
  };
  const [tipoForm, setTipoForm] = useState(initialTipoForm);
  const [campoForm, setCampoForm] = useState(initialCampoForm);
  const [selectedTipoCustom, setSelectedTipoCustom] = useState(null);
  const [customForm, setCustomForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Upload de arquivo para tipo de verificação
  const [tipoUploading, setTipoUploading] = useState(false);
  const tipoFileInputRef = useRef(null);
  
  // Upload de arquivo para verificação custom
  const [customArquivo, setCustomArquivo] = useState(null);
  const [customUploading, setCustomUploading] = useState(false);
  const customFileInputRef = useRef(null);
  
  // Confirmação de exclusão com senha admin
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null); // { type: 'verificacao' | 'tipo', id: string }
  
  // Filtro de relatório
  const [relatorioFiltro, setRelatorioFiltro] = useState("all"); // all, item, rotina
  const [relatorioItemId, setRelatorioItemId] = useState("");
  const [relatorioRotinaId, setRelatorioRotinaId] = useState("");

  // Estado para diálogo de confirmação de registro
  const [showConfirmacaoDialog, setShowConfirmacaoDialog] = useState(false);
  const [rotinaRecemCriada, setRotinaRecemCriada] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsData, verificacoesData, itensData, tiposData, setoresData] = await Promise.all([
        apiRequest("/verificacoes/stats/resumo"),
        apiRequest("/verificacoes"),
        apiRequest("/itens"),
        apiRequest("/verificacoes/tipos"),
        apiRequest("/setores").catch(() => []),
      ]);
      setStats(statsData);
      setVerificacoes(verificacoesData);
      setItens(itensData);
      setTiposCustom(tiposData);
      setSetores(setoresData || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificacoes = async () => {
    try {
      let url = "/verificacoes?";
      
      // Tratar o tipo de filtro
      if (tipoFiltro !== "all") {
        if (tipoFiltro.startsWith("custom_")) {
          // E um tipo customizado específico
          const tipoId = tipoFiltro.replace("custom_", "");
          url += `tipo_id=${tipoId}&`;
        } else {
          // E balanca ou termohigrometro
          url += `tipo=${tipoFiltro}&`;
        }
      }
      
      if (dataInicio) url += `data_inicio=${dataInicio}&`;
      if (dataFim) url += `data_fim=${dataFim}&`;
      
      const data = await apiRequest(url);
      setVerificacoes(data);
    } catch (error) {
      toast.error("Erro ao carregar verificacoes");
    }
  };

  // Exportar para Excel
  const exportarExcel = async () => {
    if (verificacoes.length === 0) {
      toast.error("Nenhuma verificação para exportar");
      return;
    }
    
    setExportandoExcel(true);
    try {
      // Preparar dados para Excel
      const dadosExcel = verificacoes.map(v => ({
        'Data': formatDate(v.data_verificacao),
        'Hora': v.hora_verificacao,
        'Tipo': v.tipo === 'balanca' ? 'Balança' : 
                v.tipo === 'termohigrometro' ? 'Termohigrômetro' : 
                v.tipo_nome || v.tipo,
        'Equipamento': v.item_nome || '-',
        'Conforme': v.conforme ? 'Sim' : 'Não',
        'Responsável': v.responsavel || '-',
        'Observações': v.observacoes || '-',
        // Dados específicos de balança
        'Peso Padrão': v.dados?.peso_padrao ?? '',
        'Peso Indicado': v.dados?.peso_indicado ?? '',
        'Diferença': v.dados?.diferenca ?? '',
        // Dados específicos de termohigrômetro
        'Temperatura Mínima': v.dados?.temperatura_min ?? '',
        'Temperatura Máxima': v.dados?.temperatura_max ?? '',
        'Temperatura Atual': v.dados?.temperatura_lida ?? '',
        'Umidade': v.dados?.umidade_lida ?? '',
      }));

      // Criar CSV
      const headers = Object.keys(dadosExcel[0]);
      const csvContent = [
        headers.join(';'),
        ...dadosExcel.map(row => headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(';'))
      ].join('\n');

      // Adicionar BOM para UTF-8
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verificacoes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel exportado com sucesso!");
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error("Erro ao exportar Excel");
    } finally {
      setExportandoExcel(false);
    }
  };

  // Gerar PDF direto (sem filtros adicionais)
  const gerarPdfDireto = async () => {
    if (verificacoes.length === 0) {
      toast.error("Nenhuma verificação para gerar PDF");
      return;
    }
    
    setExportandoPdfDireto(true);
    try {
      let url = `${API_URL}/api/pdf/verificacoes?`;
      
      if (tipoFiltro !== "all") {
        if (tipoFiltro.startsWith("custom_")) {
          const tipoId = tipoFiltro.replace("custom_", "");
          url += `tipo_id=${tipoId}&`;
        } else {
          url += `tipo=${tipoFiltro}&`;
        }
      }
      
      if (dataInicio) url += `data_inicio=${dataInicio}&`;
      if (dataFim) url += `data_fim=${dataFim}&`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (!response.ok) throw new Error('Erro ao gerar PDF');
      
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = `relatorio_verificacoes_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlBlob);
      
      toast.success("PDF gerado com sucesso!");
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error("Erro ao gerar PDF");
    } finally {
      setExportandoPdfDireto(false);
    }
  };

  // Balanca handlers
  const handleBalancaChange = (field, value) => {
    const newForm = { ...balancaForm, [field]: value };
    if (field === "peso_padrao" || field === "peso_indicado") {
      const padrão = parseFloat(field === "peso_padrao" ? value : newForm.peso_padrao) || 0;
      const indicado = parseFloat(field === "peso_indicado" ? value : newForm.peso_indicado) || 0;
      newForm.diferenca = (indicado - padrão).toFixed(4);
    }
    setBalancaForm(newForm);
  };

  const handleSaveBalanca = async () => {
    if (!balancaForm.item_id || !balancaForm.peso_padrao || !balancaForm.peso_indicado || !balancaForm.responsavel) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    setSaving(true);
    try {
      await apiRequest("/verificacoes/balanca", {
        method: "POST",
        body: JSON.stringify({
          ...balancaForm,
          peso_padrao: parseFloat(balancaForm.peso_padrao),
          peso_indicado: parseFloat(balancaForm.peso_indicado),
          diferenca: parseFloat(balancaForm.diferenca),
        }),
      });
      toast.success("Verificação de balanca registrada!");
      setBalancaDialogOpen(false);
      setBalancaForm(initialBalancaForm());
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao salvar verificação");
    } finally {
      setSaving(false);
    }
  };

  // Termo handlers
  const handleSaveTermo = async () => {
    if (!termoForm.item_id || !termoForm.temperatura_lida || !termoForm.temperatura_min || !termoForm.temperatura_max || !termoForm.umidade_lida || !termoForm.responsavel) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (parseFloat(termoForm.temperatura_min) > parseFloat(termoForm.temperatura_max)) {
      toast.error("Temperatura mínima não pode ser maior que a máxima");
      return;
    }
    setSaving(true);
    try {
      const resp = await apiRequest("/verificacoes/termohigrometro", {
        method: "POST",
        body: JSON.stringify({
          ...termoForm,
          temperatura_lida: parseFloat(termoForm.temperatura_lida),
          umidade_lida: parseFloat(termoForm.umidade_lida),
          temperatura_min: termoForm.temperatura_min ? parseFloat(termoForm.temperatura_min) : null,
          temperatura_max: termoForm.temperatura_max ? parseFloat(termoForm.temperatura_max) : null,
          umidade_min: termoForm.umidade_min ? parseFloat(termoForm.umidade_min) : null,
          umidade_max: termoForm.umidade_max ? parseFloat(termoForm.umidade_max) : null,
        }),
      });
      if (resp?.fora_faixa?.length > 0) {
        toast.warning("Registrado como NÃO CONFORME: leitura fora da faixa permitida");
      } else {
        toast.success("Verificação de termohigrometro registrada!");
      }
      setTermoDialogOpen(false);
      setTermoForm(initialTermoForm());
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao salvar verificação");
    } finally {
      setSaving(false);
    }
  };

  // Tipo Custom handlers
  const handleAddCampo = () => {
    if (!campoForm.nome) {
      toast.error("Informe o nome do campo");
      return;
    }
    const novoCampo = {
      ...campoForm,
      opcoes: campoForm.tipo === "select" ? campoForm.opcoes.split(",").map(o => o.trim()).filter(o => o) : [],
    };
    setTipoForm({ ...tipoForm, campos: [...tipoForm.campos, novoCampo] });
    setCampoForm(initialCampoForm);
  };

  const handleRemoveCampo = (index) => {
    setTipoForm({ ...tipoForm, campos: tipoForm.campos.filter((_, i) => i !== index) });
  };

  // Abrir dialog para editar tipo
  const handleEditTipo = (tipo) => {
    setEditingTipoId(tipo.id);
    setTipoForm({
      nome: tipo.nome,
      descricao: tipo.descricao || "",
      periodicidade: tipo.periodicidade || "diaria_1x",
      dias_semana: tipo.dias_semana?.length ? tipo.dias_semana : DIAS_SEG_SEX,
      dia_semana: tipo.dia_semana || "seg",
      dia_mes: tipo.dia_mes || 1,
      area: tipo.area || "",
      campos: tipo.campos || [],
      cor: tipo.cor || "slate",
      ativo: tipo.ativo !== false,
      tipo_sistema: tipo.tipo_sistema || null, // Preservar tipo de sistema
      arquivo_url: tipo.arquivo_url || "",
      arquivo_nome: tipo.arquivo_nome || "",
    });
    setTipoDialogOpen(true);
  };

  const handleSaveTipo = async () => {
    if (!tipoForm.nome) {
      toast.error("Informe o nome do tipo de verificação");
      return;
    }
    setSaving(true);
    try {
      // Preparar dados para envio, incluindo tipo_sistema se existir
      const dadosEnvio = {
        nome: tipoForm.nome,
        descricao: tipoForm.descricao,
        area: tipoForm.area,
        campos: tipoForm.campos,
        cor: tipoForm.cor,
        ativo: tipoForm.ativo,
        periodicidade: tipoForm.periodicidade,
        dias_semana: tipoForm.dias_semana?.length ? tipoForm.dias_semana : DIAS_SEG_SEX,
        dia_semana: tipoForm.dia_semana || "seg",
        dia_mes: tipoForm.dia_mes || 1,
        arquivo_url: tipoForm.arquivo_url || null,
        arquivo_nome: tipoForm.arquivo_nome || null,
      };
      
      // Preservar tipo_sistema se estiver editando um tipo de sistema
      if (tipoForm.tipo_sistema) {
        dadosEnvio.tipo_sistema = tipoForm.tipo_sistema;
      }

      if (editingTipoId) {
        // Editar tipo existente
        await apiRequest(`/verificacoes/tipos/${editingTipoId}`, {
          method: "PUT",
          body: JSON.stringify(dadosEnvio),
        });
        toast.success("Tipo de verificação atualizado!");
        setTipoDialogOpen(false);
        setTipoForm(initialTipoForm);
        setEditingTipoId(null);
      } else {
        // Criar novo tipo
        const novaRotina = await apiRequest("/verificacoes/tipos", {
          method: "POST",
          body: JSON.stringify(dadosEnvio),
        });
        
        // Verificar se a periodicidade é diária e se deveria ter sido feita hoje
        const periodicidade = tipoForm.periodicidade || "diaria_1x";
        const ehDiaria = periodicidade.startsWith("diaria");
        
        if (ehDiaria && tipoForm.ativo) {
          // É uma rotina diária - perguntar se já realizou hoje
          setRotinaRecemCriada({
            ...novaRotina,
            nome: tipoForm.nome,
            periodicidade: periodicidade,
          });
          setTipoDialogOpen(false);
          setTipoForm(initialTipoForm);
          setShowConfirmacaoDialog(true);
          toast.success("Rotina criada!");
          fetchAllData();
          return;
        }
        
        toast.success("Tipo de verificação criado!");
        setTipoDialogOpen(false);
        setTipoForm(initialTipoForm);
      }
      setEditingTipoId(null);
      fetchAllData();
    } catch (error) {
      toast.error(editingTipoId ? "Erro ao atualizar tipo" : "Erro ao criar tipo");
    } finally {
      setSaving(false);
    }
  };

  // Marcar rotina recém-criada como já realizada
  const handleConfirmarRotinaRealizada = async () => {
    if (!rotinaRecemCriada) return;
    
    try {
      // Registrar verificação para esta rotina
      const agora = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
      const pad = (n) => String(n).padStart(2, "0");
      await apiRequest("/verificacoes/custom", {
        method: "POST",
        body: JSON.stringify({
          tipo_verificacao_id: rotinaRecemCriada.id,
          data_verificacao: `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}`,
          hora_verificacao: `${pad(agora.getHours())}:${pad(agora.getMinutes())}`,
          valores: {},
          conforme: true,
          responsavel: user?.nome || "Responsável",
          observacoes: "Registrado na criação da rotina",
        }),
      });
      toast.success("Rotina marcada como realizada hoje!");
      fetchAllData();
    } catch (error) {
      const detail = error?.detail || error?.message;
      toast.error(typeof detail === "string" ? detail : "Erro ao registrar rotina");
    }
  };

  // Ignorar rotina do dia atual
  const handleIgnorarRotinaHoje = async () => {
    // Não precisa fazer nada - o backend já ignora rotinas criadas hoje
    toast.info("O sistema começará a alertar amanhã.");
  };

  const handleCloseTipoDialog = () => {
    setTipoDialogOpen(false);
    setTipoForm(initialTipoForm);
    setEditingTipoId(null);
  };

  // Upload de arquivo para tipo de verificação
  const handleTipoFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 10MB.");
      return;
    }

    setTipoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      if (!response.ok) throw new Error("Erro no upload");

      const result = await response.json();
      setTipoForm(prev => ({
        ...prev,
        arquivo_url: result.url,
        arquivo_nome: file.name,
      }));
      toast.success("Arquivo enviado!");
    } catch (error) {
      toast.error("Erro ao enviar arquivo");
    } finally {
      setTipoUploading(false);
    }
  };

  // Solicitar confirmação de senha para excluir tipo
  const handleDeleteTipoClick = (tipoId) => {
    setDeleteAction({ type: 'tipo', id: tipoId });
    setShowPasswordDialog(true);
  };

  const handleDeleteTipo = async () => {
    if (!deleteAction || deleteAction.type !== 'tipo') return;
    try {
      await apiRequest(`/verificacoes/tipos/${deleteAction.id}`, { method: "DELETE" });
      toast.success("Tipo excluido!");
      setDeleteTipoId(null);
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao excluir tipo");
    } finally {
      setDeleteAction(null);
    }
  };

  // Custom verification handlers
  const openCustomDialog = (tipo) => {
    setSelectedTipoCustom(tipo);
    const initialValues = { conforme: true, responsavel: "", observacoes: "" };
    // Garantir que campos existe e é um array
    const campos = tipo.campos || [];
    campos.forEach(campo => {
      initialValues[campo.nome] = campo.tipo === "checkbox" ? false : "";
    });
    setCustomForm({
      ...initialValues,
      data_verificacao: hojeBrasil(),
      hora_verificacao: horaBrasil(),
    });
    setCustomArquivo(null); // Reset arquivo
    setCustomDialogOpen(true);
  };

  const openCustomVerificacao = (tipo) => {
    setSelectedTipoCustom(tipo);
    setCustomForm({
      tipo_verificacao_id: tipo.id,
      data_verificacao: hojeBrasil(),
      hora_verificacao: horaBrasil(),
      campos: {},
      conforme: true,
      responsavel: "",
      observacoes: "",
    });
    setCustomArquivo(null); // Reset arquivo
    setCustomDialogOpen(true);
  };

  const handleSaveCustom = async () => {
    if (!customForm.responsavel) {
      toast.error("Informe o responsável");
      return;
    }
    // Verificar campos obrigatórios - garantir que campos existe
    const campos = selectedTipoCustom?.campos || [];
    for (const campo of campos) {
      if (campo.obrigatorio && !customForm[campo.nome]) {
        toast.error(`Campo "${campo.nome}" e obrigatório`);
        return;
      }
    }
    setSaving(true);
    try {
      let arquivo_url = null;
      let arquivo_nome = null;
      
      // Se houver arquivo selecionado, fazer upload primeiro
      if (customArquivo) {
        setCustomUploading(true);
        const formData = new FormData();
        formData.append("file", customArquivo);
        
        const uploadResponse = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${getToken()}`
          },
          body: formData
        });
        
        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.detail || "Erro ao fazer upload do arquivo");
        }
        
        const uploadData = await uploadResponse.json();
        arquivo_url = uploadData.url;
        arquivo_nome = uploadData.filename;
        setCustomUploading(false);
      }
      
      const valores = {};
      campos.forEach(campo => {
        valores[campo.nome] = customForm[campo.nome];
      });
      
      await apiRequest("/verificacoes/custom", {
        method: "POST",
        body: JSON.stringify({
          tipo_verificacao_id: selectedTipoCustom.id,
          data_verificacao: customForm.data_verificacao,
          hora_verificacao: customForm.hora_verificacao,
          valores,
          conforme: customForm.conforme,
          responsavel: customForm.responsavel,
          observacoes: customForm.observacoes,
          arquivo_url,
          arquivo_nome,
        }),
      });
      toast.success("Verificação registrada!");
      setCustomDialogOpen(false);
      setSelectedTipoCustom(null);
      setCustomArquivo(null);
      fetchAllData();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error(error.message || "Erro ao salvar verificação");
    } finally {
      setSaving(false);
      setCustomUploading(false);
    }
  };

  // Solicitar confirmação de senha para excluir verificação
  const handleDeleteClick = (verificacaoId) => {
    setDeleteAction({ type: 'verificacao', id: verificacaoId });
    setShowPasswordDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteAction || deleteAction.type !== 'verificacao') return;
    try {
      await apiRequest(`/verificacoes/${deleteAction.id}`, { method: "DELETE" });
      toast.success("Verificação excluida!");
      setDeleteId(null);
      fetchAllData();
    } catch (error) {
      toast.error("Erro ao excluir verificação");
    } finally {
      setDeleteAction(null);
    }
  };

  // Função unificada para confirmar exclusão com senha
  const handleConfirmDelete = async () => {
    if (!deleteAction) return;
    if (deleteAction.type === 'verificacao') {
      await handleDelete();
    } else if (deleteAction.type === 'tipo') {
      await handleDeleteTipo();
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      let url = `${API_URL}/api/pdf/verificacoes?`;
      
      // Tratar o tipo de filtro
      if (tipoFiltro !== "all") {
        if (tipoFiltro.startsWith("custom_")) {
          // E um tipo customizado específico
          const tipoId = tipoFiltro.replace("custom_", "");
          url += `tipo_id=${tipoId}&`;
        } else {
          // E balanca ou termohigrometro
          url += `tipo=${tipoFiltro}&`;
        }
      }
      
      if (dataInicio) url += `data_inicio=${dataInicio}&`;
      if (dataFim) url += `data_fim=${dataFim}&`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      if (!response.ok) throw new Error("Erro ao gerar PDF");

      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `verificacoes_${new Date().toISOString().split("T")[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success("PDF gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    } finally {
      setDownloading(false);
    }
  };

  // Filtrar itens ativos para os atalhos
  const itensAtivos = itens.filter(i => i.ativo !== false);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-slide-in" data-testid="verificacoes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Verificações Diárias
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Aqui aparecem os itens calibráveis que foram cadastrados e você também consegue criar rotinas diárias para a equipe executar, como por exemplo Limpeza de Banheiro, limpeza da copa, limpeza de filtros e outras rotinas necessárias.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" onClick={() => setTipoDialogOpen(true)} data-testid="nova-verificação-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Verificação
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} disabled={downloading} className="w-full sm:w-auto" data-testid="download-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Gerar PDF
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="Verificações Hoje" value={stats?.hoje?.total || 0} icon={Clock} color="teal" />
          <StatCard title="Conformes Hoje" value={stats?.hoje?.conformes || 0} icon={CheckCircle2} color="emerald" />
          <StatCard title="Não Conformes Hoje" value={stats?.hoje?.nao_conformes || 0} icon={XCircle} color="red" />
          <StatCard title="Total Geral" value={stats?.geral?.total || 0} subtitle={`${tiposCustom.length} tipos customizados`} icon={FileText} color="blue" />
        </div>

        {/* Quick Actions - Based on Calibrated Items */}
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
            Atalhos Rápidos
          </h3>
          {itensAtivos.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="p-8 text-center">
                <Scale className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                  Nenhum item calibravel cadastrado
                </h3>
                <p className="text-slate-500 max-w-md mx-auto mb-4">
                  Cadastre itens na aba "Itens Calibráveis" para criar atalhos de verificação rápida.
                </p>
                <Button variant="outline" onClick={() => window.location.href = '/itens'}>
                  Ir para Itens Calibráveis
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {itensAtivos.map((item) => {
                // Identificar tipo pelo nome do item ou categoria (case insensitive)
                const nomeItemLower = (item.nome_item || "").toLowerCase();
                const categoriaLower = (item.categoria || "").toLowerCase();
                
                // Verificar se e termohigrometro pelo nome do item ou categoria
                const isThermo = 
                  nomeItemLower.includes("termo") || 
                  nomeItemLower.includes("thermo") || 
                  nomeItemLower.includes("thermoh") ||
                  nomeItemLower.includes("higrometro") ||
                  nomeItemLower.includes("higrometro") ||
                  categoriaLower.includes("termo") || 
                  categoriaLower.includes("thermo") || 
                  categoriaLower.includes("higrometro");
                
                // Verificar se e balanca pelo nome do item ou categoria
                const isBalanca = 
                  nomeItemLower.includes("balanca") || 
                  nomeItemLower.includes("balanca") ||
                  categoriaLower.includes("balanca") || 
                  categoriaLower.includes("balanca") ||
                  categoriaLower.includes("equipamento");
                
                // Definir cores baseadas no tipo
                let corBg = "bg-slate-50";
                let corText = "text-slate-600";
                let corBtn = "bg-slate-600 hover:bg-slate-700";
                let IconComponent = Scale;

                if (isThermo) {
                  corBg = "bg-amber-50";
                  corText = "text-amber-600";
                  corBtn = "bg-amber-600 hover:bg-amber-700";
                  IconComponent = Thermometer;
                } else if (isBalanca) {
                  corBg = "bg-teal-50";
                  corText = "text-teal-600";
                  corBtn = "bg-teal-600 hover:bg-teal-700";
                  IconComponent = Scale;
                }

                const handleCardClick = () => {
                  if (isThermo) {
                    setTermoForm({ ...initialTermoForm(), item_id: item.id });
                    setTermoDialogOpen(true);
                  } else {
                    // Para balancas e outros equipamentos
                    setBalancaForm({ ...initialBalancaForm(), item_id: item.id });
                    setBalancaDialogOpen(true);
                  }
                };

                // Obter nome do setor de forma amigavel
                const setoresLabels = {
                  sala_limpa: "Sala Limpa",
                  estoque: "Estoque",
                  laboratorio: "Laboratório",
                  geladeira: "Geladeira",
                  outro: "Outro",
                };
                let setorDisplay = "-";
                if (item.setor) {
                  // Verificar se é um setor padrão
                  if (setoresLabels[item.setor]) {
                    setorDisplay = setoresLabels[item.setor];
                  } else if (item.setor.includes('-') && item.setor.length > 20) {
                    // É um UUID, buscar na lista de setores ou usar nome genérico
                    const setorFound = setores.find(s => s.value === item.setor || s.id === item.setor);
                    setorDisplay = setorFound ? setorFound.label || setorFound.nome : "Setor";
                  } else {
                    setorDisplay = item.setor;
                  }
                }

                return (
                  <Card key={item.id} className="hover:shadow-md transition-shadow cursor-pointer group" onClick={handleCardClick}>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${corBg}`}>
                        <IconComponent className={`w-6 h-6 ${corText}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{item.nome_item}</h3>
                        <p className="text-sm text-slate-500">
                          {item.identificacao_interna ? `${item.identificacao_interna} • ` : ""}{setorDisplay}
                        </p>
                      </div>
                      <Button size="sm" className={corBtn} data-testid={`verificar-${item.id}-btn`}>
                        <Plus className="w-4 h-4 mr-1" />
                        Verificar
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Atalhos para Tipos de Verificação Customizados */}
          {tiposCustom.filter(t => t.ativo !== false && !t.tipo_sistema).length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-slate-500 mb-3">Verificações Personalizadas</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {tiposCustom.filter(t => t.ativo !== false && !t.tipo_sistema).map((tipo) => {
                  const corClasses = getCorClass(tipo.cor);
                  const [bgClass, textClass] = corClasses.split(" ");
                  
                  // Obter label da periodicidade
                  const periodicidadeLabel = PERIODICIDADES_ROTINA.find(p => p.value === tipo.periodicidade)?.label || tipo.periodicidade || "";
                  const programacao = descreverProgramacao(tipo);
                  
                  return (
                    <Card 
                      key={tipo.id} 
                      className="hover:shadow-md transition-shadow group"
                    >
                      <CardContent className="p-5 flex items-center gap-4">
                        <div 
                          className={`p-3 rounded-xl ${bgClass} cursor-pointer`}
                          onClick={() => openCustomVerificacao(tipo)}
                        >
                          <ClipboardCheck className={`w-6 h-6 ${textClass}`} />
                        </div>
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => openCustomVerificacao(tipo)}
                        >
                          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{tipo.nome}</h3>
                          <p className="text-sm text-slate-500" data-testid={`rotina-programacao-${tipo.id}`}>
                            {tipo.area ? `${tipo.area} • ` : ""}{periodicidadeLabel || tipo.descricao || "Verificação personalizada"}{programacao ? ` • ${programacao}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            className={`${bgClass.replace('50', '600')} hover:${bgClass.replace('50', '700')} text-white`}
                            onClick={() => openCustomVerificacao(tipo)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Verificar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-slate-500 hover:text-slate-800"
                            onClick={() => handleEditTipo(tipo)}
                            title="Editar rotina"
                            data-testid={`rotina-editar-${tipo.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteTipoClick(tipo.id)}
                            title="Excluir tipo de verificação"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Seção de Relatórios e Historico */}
        <Card className="border-2 border-slate-200">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-xl flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <FileText className="w-5 h-5 text-teal-600" />
                  Relatório de Verificações
                </CardTitle>
                <CardDescription className="mt-1">
                  Selecione o tipo de verificação e o período para gerar o relatório
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          
          {/* Filtros */}
          <CardContent className="p-4 bg-slate-50/50 border-b">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs text-slate-500 mb-1 block">Tipo de Verificação</Label>
                  <Select value={tipoFiltro} onValueChange={setTipoFiltro}>
                    <SelectTrigger className="w-full bg-white" data-testid="filtro-tipo">
                      <SelectValue placeholder="Selecione o tipo de verificação" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">📋 Todos os tipos</SelectItem>
                      <SelectItem value="balanca">⚖️ Verificação de Balanca</SelectItem>
                      <SelectItem value="termohigrometro">🌡️ Verificação de Termohigrometro</SelectItem>
                      {tiposCustom.filter(t => !t.tipo_sistema).map((tipo) => (
                        <SelectItem key={tipo.id} value={`custom_${tipo.id}`}>
                          📝 {tipo.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Data Inicial</Label>
                  <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} className="w-full bg-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Data Final</Label>
                  <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} className="w-full bg-white" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="default" onClick={fetchVerificacoes} className="bg-slate-700 hover:bg-slate-800">
                  <Search className="w-4 h-4 mr-2" />Filtrar
                </Button>
                <Button variant="outline" onClick={() => { setTipoFiltro("all"); setDataInicio(""); setDataFim(""); fetchAllData(); }}>
                  <RefreshCw className="w-4 h-4 mr-2" />Limpar
                </Button>
              </div>
            </div>
          </CardContent>

          {/* Tabela de Historico */}
          <CardContent className="p-0">
            <div className="p-4 border-b bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  Histórico de Verificações
                </h3>
                <p className="text-sm text-slate-500">
                  {verificacoes.length} verificação(ões) encontrada(s)
                  {dataInicio && ` a partir de ${formatDate(dataInicio)}`}
                  {dataFim && ` até ${formatDate(dataFim)}`}
                </p>
              </div>
              
              {/* Botões de Exportação */}
              {verificacoes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setVisualizarModalOpen(true)}
                    data-testid="visualizar-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Visualizar
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={exportarExcel}
                    disabled={exportandoExcel}
                    className="text-green-700 border-green-300 hover:bg-green-50"
                    data-testid="exportar-excel-btn"
                  >
                    <TableIcon className="w-4 h-4 mr-2" />
                    {exportandoExcel ? 'Exportando...' : 'Excel'}
                  </Button>
                  <Button 
                    onClick={gerarPdfDireto}
                    disabled={exportandoPdfDireto}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="exportar-pdf-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    {exportandoPdfDireto ? 'Gerando...' : 'PDF'}
                  </Button>
                </div>
              )}
            </div>
            {verificacoes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Nenhuma verificação encontrada</h3>
                <p className="text-slate-500 mt-1">Comece registrando sua primeira verificação</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Data</TableHead>
                    <TableHead className="font-semibold">Hora</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Equipamento / Detalhes</TableHead>
                    <TableHead className="font-semibold">Conforme</TableHead>
                    <TableHead className="font-semibold">Responsável</TableHead>
                    <TableHead className="font-semibold">Observacoes</TableHead>
                    <TableHead className="font-semibold">Anexo</TableHead>
                    <TableHead className="w-16">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {verificacoes.map((v) => (
                    <TableRow key={v.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{formatDate(v.data_verificacao)}</TableCell>
                      <TableCell>{v.hora_verificacao}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          v.tipo === "balanca" ? "bg-teal-100 text-teal-800" : 
                          v.tipo === "termohigrometro" ? "bg-amber-100 text-amber-800" : 
                          "bg-purple-100 text-purple-800"
                        }`}>
                          {v.tipo === "balanca" ? <Scale className="w-3 h-3" /> : 
                           v.tipo === "termohigrometro" ? <Thermometer className="w-3 h-3" /> : 
                           <ClipboardCheck className="w-3 h-3" />}
                          {v.tipo === "balanca" ? "Balanca" : 
                           v.tipo === "termohigrometro" ? "Termo" : 
                           v.tipo_nome || "Custom"}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {v.tipo === "balanca" ? (
                          <div>
                            <span className="font-medium">{v.item_nome}</span>
                            <br />
                            <span className="text-slate-500">
                              Peso padrão: {v.dados?.peso_padrao?.toFixed(4)}g | 
                              Indicado: {v.dados?.peso_indicado?.toFixed(4)}g | 
                              Dif: {v.dados?.diferenca?.toFixed(4)}g
                            </span>
                          </div>
                        ) : v.tipo === "termohigrometro" ? (
                          <div>
                            <span className="font-medium">{v.item_nome}</span>
                            <br />
                            <span className="text-slate-500">
                              Mín: {v.dados?.temperatura_min?.toFixed(1) ?? '-'}°C | Máx: {v.dados?.temperatura_max?.toFixed(1) ?? '-'}°C | 
                              Atual: {v.dados?.temperatura_lida?.toFixed(1)}°C | 
                              Umidade: {v.dados?.umidade_lida?.toFixed(1)}%
                            </span>
                            {v.dados?.fora_faixa?.length > 0 && (
                              <p className="text-xs text-red-600 mt-1" data-testid="verificacao-fora-faixa">Fora da faixa: {v.dados.fora_faixa.join("; ")}</p>
                            )}
                          </div>
                        ) : (
                          <div>
                            {Object.entries(v.dados || {}).map(([k, val]) => (
                              <span key={k} className="text-slate-500 mr-2">{k}: {String(val)}</span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {v.conforme ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                            <CheckCircle2 className="w-3 h-3" /> Sim
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3" /> Não
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{v.responsavel}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">
                        {v.observacoes || "-"}
                      </TableCell>
                      <TableCell>
                        {v.arquivo_url ? (
                          <a 
                            href={`${API_URL}${v.arquivo_url}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            title={v.arquivo_nome || "Anexo"}
                          >
                            <FileText className="w-3 h-3" />
                            <span className="max-w-[80px] truncate">{v.arquivo_nome || "Ver"}</span>
                          </a>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteClick(v.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          
          {/* Rodape com info de impressão */}
          {verificacoes.length > 0 && (
            <CardContent className="p-4 bg-amber-50 border-t border-amber-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Pronto para impressão
                  </p>
                  <p className="text-xs text-amber-700">
                    Clique em "Gerar PDF para Impressão" acima para baixar o relatório completo com espaco para assinatura do responsável.
                  </p>
                </div>
                <Button 
                  onClick={handleDownloadPDF} 
                  disabled={downloading}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                >
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Baixar PDF
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Balanca Dialog */}
        <Dialog open={balancaDialogOpen} onOpenChange={setBalancaDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Scale className="w-5 h-5 text-teal-600" />
                Verificação de Balanca
              </DialogTitle>
              <DialogDescription>Registre a verificação diaria com peso padrão</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={balancaForm.data_verificacao} onChange={(e) => handleBalancaChange("data_verificacao", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={balancaForm.hora_verificacao} onChange={(e) => handleBalancaChange("hora_verificacao", e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Equipamento/Balanca *</Label>
                <Select value={balancaForm.item_id} onValueChange={(v) => handleBalancaChange("item_id", v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione o equipamento" /></SelectTrigger>
                  <SelectContent>
                    {itensAtivos.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.nome_item} {item.identificacao_interna ? `(${item.identificacao_interna})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Peso Padrão (g) *</Label>
                  <Input type="number" step="0.0001" value={balancaForm.peso_padrao} onChange={(e) => handleBalancaChange("peso_padrao", e.target.value)} placeholder="100.0000" />
                </div>
                <div className="space-y-2">
                  <Label>Peso Indicado (g) *</Label>
                  <Input type="number" step="0.0001" value={balancaForm.peso_indicado} onChange={(e) => handleBalancaChange("peso_indicado", e.target.value)} placeholder="100.0001" />
                </div>
                <div className="space-y-2">
                  <Label>Diferenca (g)</Label>
                  <Input type="text" value={balancaForm.diferenca} readOnly className="bg-slate-50" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input value={balancaForm.responsavel} onChange={(e) => handleBalancaChange("responsavel", e.target.value)} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-balanca" checked={balancaForm.conforme} onCheckedChange={(checked) => handleBalancaChange("conforme", checked)} />
                <Label htmlFor="conforme-balanca">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observacoes</Label>
                <Textarea value={balancaForm.observacoes} onChange={(e) => handleBalancaChange("observacoes", e.target.value)} placeholder="Observacoes adicionais..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBalancaDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveBalanca} className="bg-teal-600 hover:bg-teal-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Termohigrometro Dialog */}
        <Dialog open={termoDialogOpen} onOpenChange={setTermoDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Thermometer className="w-5 h-5 text-amber-600" />
                Verificação de Termohigrometro
              </DialogTitle>
              <DialogDescription>Registre a leitura de temperatura e umidade</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={termoForm.data_verificacao} onChange={(e) => setTermoForm({ ...termoForm, data_verificacao: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={termoForm.hora_verificacao} onChange={(e) => setTermoForm({ ...termoForm, hora_verificacao: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Termohigrometro *</Label>
                <Select value={termoForm.item_id} onValueChange={(v) => atualizarTermo({ item_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione o termohigrometro" /></SelectTrigger>
                  <SelectContent>
                    {itensAtivos.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.nome_item} {item.identificacao_interna ? `(${item.identificacao_interna})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {termoItemSelecionado && temFaixa(termoItemSelecionado) && (
                  <p className="text-xs text-slate-600" data-testid="termo-faixa-info">
                    Faixa permitida: {descreverFaixa(termoItemSelecionado)}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Temp. Mínima (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="termo-temp-min" value={termoForm.temperatura_min} onChange={(e) => atualizarTermo({ temperatura_min: e.target.value })} placeholder="18.0" />
                </div>
                <div className="space-y-2">
                  <Label>Temp. Máxima (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="termo-temp-max" value={termoForm.temperatura_max} onChange={(e) => atualizarTermo({ temperatura_max: e.target.value })} placeholder="25.0" />
                </div>
                <div className="space-y-2">
                  <Label>Temp. Atual (°C) *</Label>
                  <Input type="number" step="0.1" data-testid="termo-temp-atual" value={termoForm.temperatura_lida} onChange={(e) => atualizarTermo({ temperatura_lida: e.target.value })} placeholder="22.5" />
                </div>
                <div className="space-y-2">
                  <Label>Umidade (%) *</Label>
                  <Input type="number" step="0.1" data-testid="termo-umidade" value={termoForm.umidade_lida} onChange={(e) => atualizarTermo({ umidade_lida: e.target.value })} placeholder="55.0" />
                </div>
              </div>
              {termoForaFaixa.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 space-y-1" data-testid="termo-fora-faixa-alert">
                  <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Leitura fora da faixa permitida — marcada como não conforme</p>
                  <ul className="list-disc pl-5 text-xs">
                    {termoForaFaixa.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              )}
              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input data-testid="termo-responsavel" value={termoForm.responsavel} onChange={(e) => setTermoForm({ ...termoForm, responsavel: e.target.value })} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-termo" checked={termoForm.conforme} onCheckedChange={(checked) => setTermoForm({ ...termoForm, conforme: checked })} />
                <Label htmlFor="conforme-termo">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observacoes</Label>
                <Textarea value={termoForm.observacoes} onChange={(e) => setTermoForm({ ...termoForm, observacoes: e.target.value })} placeholder="Observacoes adicionais..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTermoDialogOpen(false)}>Cancelar</Button>
              <Button data-testid="termo-save-btn" onClick={handleSaveTermo} className="bg-amber-600 hover:bg-amber-700" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Criar/Editar Tipo Dialog */}
        <Dialog open={tipoDialogOpen} onOpenChange={handleCloseTipoDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Settings2 className="w-5 h-5 text-purple-600" />
                {editingTipoId ? "Editar Rotina de Verificação" : "Nova Verificação Diaria"}
              </DialogTitle>
              <DialogDescription>
                {editingTipoId 
                  ? "Edite as informações da rotina de verificação" 
                  : "Crie uma rotina personalizada (ex: Limpeza de Área, Verificação de Estoque)"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Rotina *</Label>
                  <Input value={tipoForm.nome} onChange={(e) => setTipoForm({ ...tipoForm, nome: e.target.value })} placeholder="Ex: Limpeza de Copa" data-testid="input-tipo-nome" />
                </div>
                <div className="space-y-2">
                  <Label>Cor</Label>
                  <Select value={tipoForm.cor} onValueChange={(v) => setTipoForm({ ...tipoForm, cor: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CORES.map((cor) => (
                        <SelectItem key={cor.value} value={cor.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${cor.class}`}></div>
                            {cor.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Periodicidade *</Label>
                  <Select value={tipoForm.periodicidade || "diaria_1x"} onValueChange={(v) => setTipoForm({ ...tipoForm, periodicidade: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODICIDADES_ROTINA.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Identificacao da Área</Label>
                  <Input value={tipoForm.area || ""} onChange={(e) => setTipoForm({ ...tipoForm, area: e.target.value })} placeholder="Ex: Copa, Laboratorio, Estoque" />
                </div>
              </div>

              <ProgramacaoRotina form={tipoForm} setForm={setTipoForm} />

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea value={tipoForm.descricao} onChange={(e) => setTipoForm({ ...tipoForm, descricao: e.target.value })} placeholder="Descrição opcional da rotina" rows={2} />
              </div>

              <div className="flex items-center gap-2">
                <Switch id="tipo-ativo" checked={tipoForm.ativo} onCheckedChange={(checked) => setTipoForm({ ...tipoForm, ativo: checked })} />
                <Label htmlFor="tipo-ativo">Rotina ativa</Label>
              </div>

              {/* Upload de Arquivo */}
              <div className="space-y-2">
                <Label>Arquivo/Documento (opcional)</Label>
                {tipoForm.arquivo_url ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <FileText className="w-6 h-6 text-purple-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{tipoForm.arquivo_nome}</p>
                      <a href={`${API_URL}${tipoForm.arquivo_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-600 hover:underline">Visualizar</a>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => setTipoForm({ ...tipoForm, arquivo_url: "", arquivo_nome: "" })}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center hover:border-purple-400 transition-colors cursor-pointer"
                    onClick={() => tipoFileInputRef.current?.click()}
                  >
                    <input ref={tipoFileInputRef} type="file" className="hidden" onChange={handleTipoFileUpload} />
                    {tipoUploading ? (
                      <Loader2 className="w-6 h-6 mx-auto text-purple-600 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 mx-auto text-slate-400" />
                    )}
                    <p className="mt-1 text-sm text-slate-600">{tipoUploading ? "Enviando..." : "Clique para anexar arquivo"}</p>
                  </div>
                )}
              </div>

              {/* Campos */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Campos do Formulário (opcional)</Label>
                
                {tipoForm.campos.length > 0 && (
                  <div className="space-y-2">
                    {tipoForm.campos.map((campo, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="font-medium">{campo.nome}</span>
                          <span className="text-sm text-slate-500 ml-2">({campo.tipo})</span>
                          {campo.obrigatorio && <span className="text-xs text-red-500 ml-2">*obrigatório</span>}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveCampo(index)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="p-4 border border-dashed rounded-lg space-y-3">
                  <p className="text-sm font-medium text-slate-700">Adicionar Campo Customizado</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Nome do Campo</Label>
                      <Input value={campoForm.nome} onChange={(e) => setCampoForm({ ...campoForm, nome: e.target.value })} placeholder="Ex: Área verificada" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tipo</Label>
                      <Select value={campoForm.tipo} onValueChange={(v) => setCampoForm({ ...campoForm, tipo: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Texto</SelectItem>
                          <SelectItem value="number">Número</SelectItem>
                          <SelectItem value="checkbox">Checkbox (Sim/Não)</SelectItem>
                          <SelectItem value="select">Lista de Opcoes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {campoForm.tipo === "select" && (
                    <div className="space-y-1">
                      <Label className="text-xs">Opcoes (separadas por virgula)</Label>
                      <Input value={campoForm.opcoes} onChange={(e) => setCampoForm({ ...campoForm, opcoes: e.target.value })} placeholder="Opção 1, Opção 2, Opção 3" />
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch id="campo-obrigatorio" checked={campoForm.obrigatorio} onCheckedChange={(checked) => setCampoForm({ ...campoForm, obrigatorio: checked })} />
                      <Label htmlFor="campo-obrigatorio" className="text-sm">Obrigatório</Label>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={handleAddCampo}>
                      <Plus className="w-4 h-4 mr-1" />Adicionar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseTipoDialog}>Cancelar</Button>
              <Button onClick={handleSaveTipo} className="bg-purple-600 hover:bg-purple-700" disabled={saving} data-testid="salvar-tipo-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingTipoId ? "Salvar Alteracoes" : "Criar Tipo"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Verification Dialog */}
        <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
                {selectedTipoCustom?.nome}
              </DialogTitle>
              <DialogDescription>{selectedTipoCustom?.descricao || "Registre a verificação"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input type="date" value={customForm.data_verificacao || ""} onChange={(e) => setCustomForm({ ...customForm, data_verificacao: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Hora *</Label>
                  <Input type="time" value={customForm.hora_verificacao || ""} onChange={(e) => setCustomForm({ ...customForm, hora_verificacao: e.target.value })} />
                </div>
              </div>

              {/* Dynamic Fields */}
              {selectedTipoCustom?.campos?.map((campo) => (
                <div key={campo.nome} className="space-y-2">
                  <Label>{campo.nome} {campo.obrigatorio && <span className="text-red-500">*</span>}</Label>
                  {campo.tipo === "text" && (
                    <Input value={customForm[campo.nome] || ""} onChange={(e) => setCustomForm({ ...customForm, [campo.nome]: e.target.value })} />
                  )}
                  {campo.tipo === "number" && (
                    <Input type="number" step="any" value={customForm[campo.nome] || ""} onChange={(e) => setCustomForm({ ...customForm, [campo.nome]: e.target.value })} />
                  )}
                  {campo.tipo === "checkbox" && (
                    <div className="flex items-center gap-2">
                      <Switch checked={customForm[campo.nome] || false} onCheckedChange={(checked) => setCustomForm({ ...customForm, [campo.nome]: checked })} />
                      <span className="text-sm text-slate-600">{customForm[campo.nome] ? "Sim" : "Não"}</span>
                    </div>
                  )}
                  {campo.tipo === "select" && (
                    <Select value={customForm[campo.nome] || ""} onValueChange={(v) => setCustomForm({ ...customForm, [campo.nome]: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {campo.opcoes?.map((opção) => (
                          <SelectItem key={opção} value={opção}>{opção}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              ))}

              <div className="space-y-2">
                <Label>Responsável *</Label>
                <Input value={customForm.responsavel || ""} onChange={(e) => setCustomForm({ ...customForm, responsavel: e.target.value })} placeholder="Nome do responsável" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="conforme-custom" checked={customForm.conforme || false} onCheckedChange={(checked) => setCustomForm({ ...customForm, conforme: checked })} />
                <Label htmlFor="conforme-custom">Conforme</Label>
              </div>
              <div className="space-y-2">
                <Label>Observacoes</Label>
                <Textarea value={customForm.observacoes || ""} onChange={(e) => setCustomForm({ ...customForm, observacoes: e.target.value })} placeholder="Observacoes adicionais..." rows={2} />
              </div>
              
              {/* Upload de Arquivo Opcional */}
              <div className="space-y-2">
                <Label>Anexo (opcional)</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={customFileInputRef}
                    onChange={(e) => setCustomArquivo(e.target.files[0])}
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => customFileInputRef.current?.click()}
                    className="flex items-center gap-2"
                    disabled={customUploading}
                  >
                    <Upload className="w-4 h-4" />
                    {customArquivo ? "Trocar arquivo" : "Selecionar arquivo"}
                  </Button>
                  {customArquivo && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-1 rounded">
                      <FileText className="w-4 h-4" />
                      <span className="max-w-[150px] truncate">{customArquivo.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setCustomArquivo(null);
                          if (customFileInputRef.current) customFileInputRef.current.value = "";
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-400">Formatos: PDF, PNG, JPG, DOC, DOCX, XLS, XLSX (max 10MB)</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveCustom} className="bg-purple-600 hover:bg-purple-700" disabled={saving || customUploading}>
                {(saving || customUploading) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {customUploading ? "Enviando arquivo..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Admin Password Dialog para exclusões */}
        <AdminPasswordDialog
          open={showPasswordDialog}
          onOpenChange={(open) => {
            setShowPasswordDialog(open);
            if (!open) setDeleteAction(null);
          }}
          onConfirm={handleConfirmDelete}
          title={deleteAction?.type === 'tipo' ? "Excluir Tipo de Verificação" : "Excluir Verificação"}
          description="Esta ação não pode ser desfeita. O registro será removido permanentemente."
        />

        {/* Modal de Visualização em Tela */}
        <Dialog open={visualizarModalOpen} onOpenChange={setVisualizarModalOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-teal-600" />
                Visualização de Verificações
              </DialogTitle>
              <DialogDescription>
                {verificacoes.length} registro(s) encontrado(s)
                {dataInicio && ` | De: ${formatDate(dataInicio)}`}
                {dataFim && ` até ${formatDate(dataFim)}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Data</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Hora</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Tipo</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Equipamento</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Valores</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Status</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Responsável</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {verificacoes.map((v, index) => (
                    <tr key={v.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-3 py-2">{formatDate(v.data_verificacao)}</td>
                      <td className="px-3 py-2">{v.hora_verificacao}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs">
                          {v.tipo === 'balanca' ? 'Balança' : 
                           v.tipo === 'termohigrometro' ? 'Termohigrômetro' : 
                           v.tipo_nome || v.tipo}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{v.item_nome || '-'}</td>
                      <td className="px-3 py-2 text-xs">
                        {v.tipo === 'balanca' && (
                          <>P: {v.dados?.peso_padrao}g | I: {v.dados?.peso_indicado}g | Dif: {v.dados?.diferenca}g</>
                        )}
                        {v.tipo === 'termohigrometro' && (
                          <>Mín: {v.dados?.temperatura_min ?? '-'}°C | Máx: {v.dados?.temperatura_max ?? '-'}°C | Atual: {v.dados?.temperatura_lida}°C | Umid: {v.dados?.umidade_lida}%
                            {v.dados?.fora_faixa?.length > 0 && (
                              <span className="block text-red-600" data-testid="verificacao-fora-faixa-preview">Fora da faixa: {v.dados.fora_faixa.join("; ")}</span>
                            )}
                          </>
                        )}
                        {v.tipo !== 'balanca' && v.tipo !== 'termohigrometro' && (
                          <>{v.observacoes || '-'}</>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {v.conforme ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Conforme</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 text-xs">Não Conforme</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">{v.responsavel || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={exportarExcel}
                disabled={exportandoExcel}
                className="text-green-700 border-green-300"
              >
                <TableIcon className="w-4 h-4 mr-2" />
                {exportandoExcel ? 'Exportando...' : 'Exportar Excel'}
              </Button>
              <Button 
                onClick={gerarPdfDireto}
                disabled={exportandoPdfDireto}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Printer className="w-4 h-4 mr-2" />
                {exportandoPdfDireto ? 'Gerando...' : 'Gerar PDF'}
              </Button>
              <Button variant="outline" onClick={() => setVisualizarModalOpen(false)}>
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Filtros Avançados para Relatório */}
        <RelatorioFilterModal
          open={relatorioModalOpen}
          onOpenChange={setRelatorioModalOpen}
          title="Relatório de Verificações Diárias"
          description="Selecione os filtros para gerar o relatório em PDF"
          endpoint="/pdf/verificacoes"
          filters={[
            {
              key: "tipo",
              label: "Tipo de Verificação",
              type: "select",
              options: [
                { value: "balanca", label: "Balança" },
                { value: "termohigrometro", label: "Termohigrômetro" },
                ...tiposCustom.map(t => ({ value: `custom_${t.id}`, label: t.nome }))
              ]
            },
            {
              key: "setor",
              label: "Setor/Local",
              type: "select",
              options: [
                { value: "sala_limpa", label: "Sala Limpa" },
                { value: "estoque", label: "Estoque" },
                { value: "laboratorio", label: "Laboratório" },
                { value: "geladeira", label: "Geladeira" }
              ]
            },
            {
              key: "responsavel",
              label: "Responsável",
              type: "text",
              placeholder: "Filtrar por responsável..."
            },
            {
              key: "conforme",
              label: "Conformidade",
              type: "select",
              options: [
                { value: "true", label: "Conforme" },
                { value: "false", label: "Não Conforme" }
              ]
            },
            {
              key: "data_inicio",
              label: "Data início",
              type: "date"
            },
            {
              key: "data_fim",
              label: "Data fim",
              type: "date"
            }
          ]}
        />

        {/* Dialog de confirmação de registro de rotina */}
        <ConfirmacaoRegistroDialog
          open={showConfirmacaoDialog}
          onOpenChange={setShowConfirmacaoDialog}
          onConfirmRegistrar={handleConfirmarRotinaRealizada}
          onConfirmIgnorar={handleIgnorarRotinaHoje}
          tipo="rotina"
          nomeItem={rotinaRecemCriada?.nome || ""}
          detalhes={rotinaRecemCriada?.periodicidade ? `Periodicidade: ${rotinaRecemCriada.periodicidade.replace("diaria_", "").replace("x", "x ao dia")}` : ""}
        />
      </div>
    </Layout>
  );
};

export default Verificações;
